// #region Imports

import type { CssColor } from './CssColor';
import type { CssShadow } from './CssShadow';
import type { IThemeElevation } from './IThemeElevation';
import type { IThemeLayout } from './IThemeLayout';
import type { ThemeMode } from './ThemeMode';
import { ThemePalette, type ThemeShadeName } from './ThemePalette';
import type { ThemeScheme, ThemeSchemeRole } from './ThemeScheme';
import type { ThemeSemantic, ThemeSemanticName } from './ThemeSemantic';
import type { ThemeTypography } from './ThemeTypography';

// #endregion

/**
 * Represents a dynamic theme system.
 *
 * @public
 */
export interface ITheme {

    /**
     * Human-readable theme name.
     */
    name: string;

    /**
     * Theme color schemes.
     *
     * ThemeMode → ThemeScheme
     */
    scheme: Readonly<Record<ThemeMode, ThemeScheme>>;

    /**
     * Theme color palettes.
     *
     * ThemeMode → Semantic Color → ThemePalette | CssColor
     */
    palette: Readonly<Record<ThemeMode, ThemeSemantic>>;

    /**
     * Base font family.
     */
    fontFamily: string;

    /**
     * Typography definition.
     */
    typography: ThemeTypography;

    /**
     * Layout definition.
     */
    layout: IThemeLayout;

    /**
     * Elevation definitions.
     */
    elevation: Readonly<Record<string, IThemeElevation>>;
}

/**
 * @public
 */
export namespace ITheme {

    /**
     * Gets the scheme for the specified mode.
     *
     * @param theme The theme to get the scheme from.
     * @param mode The theme mode.
     * @returns The theme scheme for the specified mode.
     */
    export function getScheme(
        theme: ITheme,
        mode: ThemeMode
    ): ThemeScheme {
        return theme.scheme[mode];
    }

    /**
     * Gets the palette for the specified semantic color and mode.
     *
     * @param theme The theme to get the palette from.
     * @param mode The theme mode.
     * @param semanticColor The semantic color name.
     * @returns The theme palette for the specified semantic color and mode.
     */
    export function getPalette(
        theme: ITheme,
        mode: ThemeMode,
        semanticColor: ThemeSemanticName
    ): ThemePalette {
        const paletteEntry = theme.palette[mode][semanticColor];

        if (!ThemePalette.isThemePalette(paletteEntry)) {
            throw new Error(
                `Palette '${semanticColor}' is not a valid theme palette for mode '${mode}'.`
            );
        }

        return paletteEntry;
    }

    /**
     * Gets a scheme color for the specified role and mode.
     *
     * @param theme The theme to get the color from.
     * @param mode The theme mode.
     * @param role The scheme role.
     * @returns The CSS color for the specified role and mode.
     */
    export function getSchemeColor(
        theme: ITheme,
        mode: ThemeMode,
        role: ThemeSchemeRole
    ): CssColor {
        const scheme = theme.scheme[mode];
        const color = scheme[role];

        if (typeof color !== 'string') {
            throw new Error(`Scheme color '${role}' is not defined for mode '${mode}'.`);
        }

        return color;
    }

    /**
     * Gets a palette color for the specified semantic color, shade, and mode.
     *
     * @param theme The theme to get the color from.
     * @param mode The theme mode.
     * @param semanticColor The semantic color name.
     * @param shade The shade name.
     * @returns The CSS color for the specified semantic color, shade, and mode.
     */
    export function getPaletteColor(
        theme: ITheme,
        mode: ThemeMode,
        semanticColor: ThemeSemanticName,
        shade: ThemeShadeName
    ): CssColor {
        const palette = getPalette(theme, mode, semanticColor);
        const color = palette[shade];

        if (typeof color !== 'string') {
            throw new Error(
                `Shade '${shade}' is not defined in palette '${semanticColor}' for mode '${mode}'.`
            );
        }

        return color;
    }

    /**
     * Gets an elevation shadow for the specified key and mode.
     *
     * @param theme The theme to get the elevation from.
     * @param mode The theme mode.
     * @param key The elevation key.
     * @returns The CSS shadow for the specified elevation key and mode.
     */
    export function getElevationShadow(
        theme: ITheme,
        mode: ThemeMode,
        key: string
    ): CssShadow {
        return theme.elevation[mode][key];
    }

}
